import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BudgetRevisionRequestModal } from "@/pages/BudgetRevisions";
import BOQItemPicker from "@/components/BOQItemPicker";

const emptyLine = () => ({
  description: "",
  quantity: 1,
  unit: "nos",
  unit_price: 0,
  tax_percent: 18,
  total: 0,
});

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

export default function POCreate({ poType = "material" }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const { activeProjectId, activeProject } = useProject();
  const isContract = poType === "contract";
  const detailBase = isContract ? "/contracts" : "/pos";

  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [loadingPO, setLoadingPO] = useState(isEdit);
  const [poNumber, setPoNumber] = useState("");
  const [editProjectId, setEditProjectId] = useState(null); // preserve on edit
  // Master descriptions attached to the picked BOQ item (or any ancestor node).
  // Each line's description becomes a Select of these variants when populated.
  const [boqVariants, setBoqVariants] = useState([]);
  const [siteLocationTouched, setSiteLocationTouched] = useState(false);

  const [form, setForm] = useState({
    vendor_id: "",
    contract_type: "",
    site_location: "",
    delivery_date: "",
    notes: "",
    terms: "Payment within 30 days of delivery. Goods to comply with specifications.",
    boq_item_id: null,
  });
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [budgetCheck, setBudgetCheck] = useState(null);
  const [revisionOpen, setRevisionOpen] = useState(false);
  // Track locked state per line — locked lines were auto-filled from a BOQ item pick
  // (description/unit/rate are read-only; user only edits quantity).
  const [lineLocked, setLineLocked] = useState([false]);

  // Auto-fill Site Location from the active project's address (create mode only, and only until user types).
  useEffect(() => {
    if (isEdit) return;
    if (siteLocationTouched) return;
    if (activeProject?.address) {
      setForm((f) => ({ ...f, site_location: activeProject.address }));
    }
  }, [activeProject?.address, isEdit, siteLocationTouched]);

  // Load master-description variants whenever a BOQ item is picked.
  useEffect(() => {
    if (!form.boq_item_id) { setBoqVariants([]); return; }
    const pid = isEdit ? editProjectId : activeProjectId;
    if (!pid) return;
    api.get("/master-descriptions/for-boq-item", {
      params: { boq_item_id: form.boq_item_id, project_id: pid },
    })
      .then((r) => setBoqVariants(r.data?.items || []))
      .catch(() => setBoqVariants([]));
  }, [form.boq_item_id, activeProjectId, editProjectId, isEdit]);

  // Apply a master-description variant to a specific line (description + unit + rate).
  const applyVariant = (idx, variant) => {
    setLines((prev) => {
      const next = [...prev];
      const line = { ...next[idx] };
      line.description = variant.description || "";
      line.unit = variant.unit || line.unit || "nos";
      line.unit_price = Number(variant.rate) || 0;
      const qty = parseFloat(line.quantity) || 0;
      const tax = parseFloat(line.tax_percent) || 0;
      const base = qty * line.unit_price;
      line.total = +(base + (base * tax) / 100).toFixed(2);
      next[idx] = line;
      return next;
    });
  };

  const handleBOQPick = (id, item) => {
    if (!id || !item) {
      // Cleared — unlock first line but keep values so user can edit
      setForm((f) => ({ ...f, boq_item_id: null }));
      setLineLocked((prev) => prev.map((_, i) => (i === 0 ? false : prev[i])));
      return;
    }
    // Autofill first line from BOQ item (contract POs also allowed to pick BOQ but not required)
    setForm((f) => ({ ...f, boq_item_id: id }));
    const rate = Number(item.rate) || 0;
    setLines((prev) => {
      const next = [...prev];
      next[0] = {
        description: item.description || item.name || "",
        quantity: 1,
        unit: item.unit || "nos",
        unit_price: rate,
        tax_percent: 18,
        total: rate * 1 * 1.18,
      };
      return next;
    });
    setLineLocked((prev) => {
      const next = [...prev];
      next[0] = true;
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      // Only fetch contract types when needed
      if (isContract) {
        const c = await api.get("/catalog/categories", { params: { type: "contract" } });
        setCategories(c.data.categories || []);
      } else {
        setCategories([]);
      }
      const v = await api.get("/vendors", { params: { page_size: 200 } });
      setVendors(Array.isArray(v.data) ? v.data : (v.data?.items || []));
    })();
  }, [poType, isContract]);

  // Load PO for edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/pos/${editId}`);
        setPoNumber(data.po_number || "");
        setEditProjectId(data.project_id || null);
        setForm({
          vendor_id: data.vendor_id || "",
          contract_type: data.po_type === "contract" ? (data.category || "") : "",
          site_location: data.site_location || "",
          delivery_date: data.delivery_date || "",
          notes: data.notes || "",
          terms: data.terms || "",
          boq_item_id: data.boq_item_id || null,
        });
        const li = (data.line_items || []).map((l) => ({
          description: l.description || "",
          quantity: String(l.quantity ?? ""),
          unit: l.unit || "nos",
          unit_price: String(l.unit_price ?? ""),
          tax_percent: String(l.tax_percent ?? 0),
          total: l.total ?? 0,
        }));
        setLines(li.length > 0 ? li : [emptyLine()]);
        // Edit mode: existing lines are always editable (no BOQ lock).
        setLineLocked((li.length > 0 ? li : [emptyLine()]).map(() => false));
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        navigate(`${detailBase}/${editId}`);
      } finally {
        setLoadingPO(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isEdit]);

  const updateLine = (idx, field, value) => {
    const updated = lines.map((line, i) => {
      if (i !== idx) return line;
      const n = { ...line, [field]: value };
      const qty = parseFloat(n.quantity) || 0;
      const price = parseFloat(n.unit_price) || 0;
      const tax = parseFloat(n.tax_percent) || 0;
      const base = qty * price;
      n.total = +(base + (base * tax) / 100).toFixed(2);
      return n;
    });
    setLines(updated);
  };

  const addLine = () => {
    setLines([...lines, emptyLine()]);
    setLineLocked([...lineLocked, false]);
  };
  const removeLine = (idx) => {
    setLines(lines.filter((_, i) => i !== idx));
    setLineLocked(lineLocked.filter((_, i) => i !== idx));
  };

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const taxTotal = lines.reduce((s, l) => {
    const base = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
    return s + (base * (parseFloat(l.tax_percent) || 0)) / 100;
  }, 0);
  const grandTotal = subtotal + taxTotal;

  const canSave =
    form.vendor_id &&
    (poType === "contract" ? !!form.contract_type : !!form.boq_item_id) &&
    lines.length > 0 &&
    lines.every((l) => l.description && parseFloat(l.quantity) > 0 && parseFloat(l.unit_price) >= 0);

  const handleSubmit = async (opts = {}) => {
    if (!canSave) {
      if (poType === "material" && !form.boq_item_id) {
        toast.error("Please pick the BOQ item this PO is against.");
      } else if (poType === "contract" && !form.contract_type) {
        toast.error("Please select a contract type.");
      } else {
        toast.error("Please fill vendor and all line items.");
      }
      return;
    }
    if (!isEdit && !activeProjectId) {
      toast.error("Select a project from the sidebar first.");
      return;
    }

    // ---- Budget preflight (only on CREATE for material POs) ----
    if (!isEdit && poType === "material" && !opts.skipBudgetCheck) {
      try {
        const { data: check } = await api.post("/pos/check-budget", {
          project_id: activeProjectId,
          boq_item_id: form.boq_item_id,
          line_items: lines.map((l) => ({
            quantity: parseFloat(l.quantity) || 0,
            unit_price: parseFloat(l.unit_price) || 0,
            unit: l.unit || "nos",
          })),
        });
        if (check.is_over_budget) {
          setBudgetCheck({ ...check, boq_item_id: form.boq_item_id });
          setRevisionOpen(true);
          return; // block submit — user must justify first
        }
      } catch (err) {
        // If the preflight endpoint fails, don't block the PO from being created
        console.warn("Budget preflight failed — allowing PO through:", err);
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        po_type: poType,
        project_id: isEdit ? editProjectId : activeProjectId,
        line_items: lines.map((l) => ({
          description: l.description,
          quantity: parseFloat(l.quantity),
          unit: l.unit || "nos",
          unit_price: parseFloat(l.unit_price),
          tax_percent: parseFloat(l.tax_percent) || 0,
          total: l.total,
        })),
        subtotal: +subtotal.toFixed(2),
        tax_amount: +taxTotal.toFixed(2),
        total_amount: +grandTotal.toFixed(2),
      };
      if (opts.budgetRevisionId) payload.budget_revision_id = opts.budgetRevisionId;
      if (isEdit) {
        const { data } = await api.put(`/pos/${editId}`, payload);
        toast.success(`PO ${data.po_number} updated`);
        navigate(`${detailBase}/${editId}`);
      } else {
        const { data } = await api.post("/pos", payload);
        if (opts.budgetRevisionId) {
          toast.success(`PO ${data.po_number} created — awaiting owner revision approval`);
        } else {
          toast.success(`PO ${data.po_number} created`);
        }
        navigate(`${detailBase}/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 sm:p-10 max-w-[1400px] mx-auto" data-testid="po-create-page">
      {loadingPO && (
        <div className="bg-white border border-neutral-200 rounded-sm p-10 text-center text-sm text-neutral-500" data-testid="po-loading">
          Loading PO…
        </div>
      )}
      {!loadingPO && (
      <>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
          {activeProject && (
            <>
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
              <span>{activeProject.name}</span>
              <span className="text-neutral-300">·</span>
            </>
          )}
          <span>{isEdit ? "Edit" : (isContract ? "New Contract" : "New Order")}</span>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight">
          {isEdit
            ? `Edit ${isContract ? "Contract " : ""}PO ${poNumber || ""}`.trim()
            : (isContract ? "Raise Contract PO" : "Raise Purchase Order")}
        </h1>
        <p className="text-sm text-neutral-500 mt-2">
          {isEdit
            ? "Update line items, vendor, or terms. Project and PO type cannot be changed. Once an invoice is uploaded, edits are locked."
            : (isContract
              ? "Select contract type, vendor, and add scope-of-work line items."
              : "Pick your BOQ item first — description, unit and rate auto-fill from the BOQ.")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Meta */}
          <div className="bg-white border border-neutral-200 rounded-sm p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Order Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isContract && (
                <div className="space-y-1.5 md:col-span-2 pb-4 border-b border-neutral-100">
                  <BOQItemPicker
                    projectId={isEdit ? editProjectId : activeProjectId}
                    value={form.boq_item_id}
                    onChange={handleBOQPick}
                    required
                    label="BOQ Item — start here (searchable, grouped by Block › Floor)"
                    testId="po-boq-picker"
                  />
                  <div className="text-[11px] text-neutral-500 leading-relaxed">
                    Pick the BOQ line this PO is against — <span className="text-neutral-700 font-medium">Description, Unit and Rate</span> will auto-fill from your BOQ. You just enter the quantity.
                  </div>
                </div>
              )}
              {isContract && (
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Contract Type *</Label>
                  <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                    <SelectTrigger className="rounded-sm h-11" data-testid="contract-type-select">
                      <SelectValue placeholder="Select contract type" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5 md:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Vendor *</Label>
                  <Dialog open={vendorOpen} onOpenChange={setVendorOpen}>
                    <DialogTrigger asChild>
                      <button type="button" className="text-xs text-primary hover:underline" data-testid="add-vendor-link">+ Quick add vendor</button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-display">Add Vendor</DialogTitle>
                      </DialogHeader>
                      <QuickVendorForm
                        onCreated={(v) => {
                          setVendors([...vendors, v]);
                          setForm({ ...form, vendor_id: v.id });
                          setVendorOpen(false);
                        }}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="vendor-select">
                    <SelectValue placeholder={vendors.length ? "Select vendor" : "No vendors yet – quick add above"} />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Site Location</Label>
                <Input
                  value={form.site_location}
                  onChange={(e) => { setForm({ ...form, site_location: e.target.value }); setSiteLocationTouched(true); }}
                  placeholder={activeProject?.address ? activeProject.address : "Site address"}
                  className="rounded-sm h-11"
                  data-testid="site-input"
                />
                {activeProject?.address && form.site_location !== activeProject.address && (
                  <button
                    type="button"
                    className="text-[11px] text-primary hover:underline mt-0.5"
                    onClick={() => { setForm({ ...form, site_location: activeProject.address }); setSiteLocationTouched(false); }}
                    data-testid="site-use-project-address"
                  >
                    Use project address
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Expected Delivery</Label>
                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="rounded-sm h-11" data-testid="delivery-date-input" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Notes / Specifications</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm min-h-[80px]" data-testid="notes-input" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-neutral-600">Terms & Conditions</Label>
                <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="rounded-sm min-h-[60px]" data-testid="terms-input" />
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="bg-white border border-neutral-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Line Items</div>
              <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1" onClick={addLine} data-testid="add-line-btn">
                <Plus size={14} /> Add Item
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium">Description</th>
                    <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-medium w-20">Qty</th>
                    <th className="text-left px-2 py-2 text-xs uppercase tracking-wider font-medium w-20">Unit</th>
                    <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-medium w-28">Rate</th>
                    <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-medium w-20">Tax %</th>
                    <th className="text-right px-2 py-2 text-xs uppercase tracking-wider font-medium w-28">Total</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const locked = !!lineLocked[i];
                    return (
                    <tr key={i} className={"border-t border-neutral-100 " + (locked ? "bg-primary/5" : "")} data-testid={`line-${i}`}>
                      <td className="px-3 py-2">
                        {locked ? (
                          <div className="space-y-1">
                            <div className="rounded-sm h-9 px-2 flex items-center bg-neutral-50 border border-neutral-200 text-sm text-neutral-700" data-testid={`line-${i}-description-locked`}>
                              <span className="text-[9px] uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm mr-2 flex-shrink-0">BOQ</span>
                              <span className="truncate">{l.description || <span className="text-neutral-400">—</span>}</span>
                            </div>
                            {boqVariants.length > 0 && (
                              <Select
                                value={boqVariants.find((v) => v.description === l.description) ? l.description : ""}
                                onValueChange={(val) => {
                                  const v = boqVariants.find((x) => x.description === val);
                                  if (v) applyVariant(i, v);
                                }}
                              >
                                <SelectTrigger className="rounded-sm h-7 text-[11px] border-dashed" data-testid={`line-${i}-variant-select`}>
                                  <SelectValue placeholder={`Pick from ${boqVariants.length} linked variant(s)`} />
                                </SelectTrigger>
                                <SelectContent>
                                  {boqVariants.map((v) => (
                                    <SelectItem key={v.id} value={v.description} data-testid={`line-${i}-variant-${v.id}`}>
                                      <div className="flex flex-col">
                                        <span>{v.description}</span>
                                        <span className="text-[10px] text-neutral-500">{v.unit} · ₹{v.rate}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        ) : (
                          <Input value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)} placeholder="Material description" className="rounded-sm h-9" data-testid={`line-${i}-description`} />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <Input type="number" min={0} step="0.01" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="rounded-sm h-9 text-right tabular-nums" data-testid={`line-${i}-qty`} />
                      </td>
                      <td className="px-2 py-2">
                        {locked ? (
                          <div className="rounded-sm h-9 px-2 flex items-center bg-neutral-50 border border-neutral-200 text-sm text-neutral-700" data-testid={`line-${i}-unit-locked`}>{l.unit}</div>
                        ) : (
                          <Input value={l.unit} onChange={(e) => updateLine(i, "unit", e.target.value)} placeholder="nos" className="rounded-sm h-9" data-testid={`line-${i}-unit`} />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {locked ? (
                          <div className="rounded-sm h-9 px-2 flex items-center justify-end bg-neutral-50 border border-neutral-200 text-sm text-neutral-700 tabular-nums" data-testid={`line-${i}-price-locked`}>{Number(l.unit_price).toFixed(2)}</div>
                        ) : (
                          <Input type="number" min={0} step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} className="rounded-sm h-9 text-right tabular-nums" data-testid={`line-${i}-price`} />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <Input type="number" min={0} step="0.01" value={l.tax_percent} onChange={(e) => updateLine(i, "tax_percent", e.target.value)} className="rounded-sm h-9 text-right tabular-nums" data-testid={`line-${i}-tax`} />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium">{fmt(l.total)}</td>
                      <td className="px-2 py-2">
                        {lines.length > 1 && !locked && (
                          <button onClick={() => removeLine(i)} className="text-neutral-400 hover:text-destructive p-1" data-testid={`line-${i}-remove`}>
                            <Trash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right: summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-sm p-6 lg:sticky lg:top-8">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Summary</div>
            <div className="space-y-2 text-sm mb-6">
              <Row label="Subtotal" value={fmt(subtotal)} />
              <Row label="Tax" value={fmt(taxTotal)} />
              <div className="border-t border-neutral-200 pt-2 mt-2">
                <Row label="Grand Total" value={fmt(grandTotal)} bold />
              </div>
            </div>
            <Button
              className="w-full rounded-sm h-11 bg-primary hover:bg-[#0039CC] text-white font-medium"
              onClick={handleSubmit}
              disabled={!canSave || saving}
              data-testid="save-po-btn"
            >
              {saving ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save Changes" : isContract ? "Raise Contract PO →" : "Raise Purchase Order →"}
            </Button>
            <p className="text-xs text-neutral-500 mt-3 leading-relaxed">
              {isContract
                ? "Contract POs follow the same flow: upload the contractor's tax invoice on the PO detail page; AI validates qty and price against the awarded scope."
                : "After creation, upload the vendor's tax invoice on the PO detail page. Our AI validates quantity and price against this PO."}
            </p>
          </div>
        </div>
      </div>
      <BudgetRevisionRequestModal
        open={revisionOpen}
        onOpenChange={(o) => { setRevisionOpen(o); if (!o) setBudgetCheck(null); }}
        check={budgetCheck}
        category={budgetCheck?.category || ""}
        projectId={activeProjectId}
        onCreated={(rev) => handleSubmit({ skipBudgetCheck: true, budgetRevisionId: rev.id })}
      />
      </>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-display font-bold text-base" : "text-neutral-600"}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-display font-bold text-base" : ""}`}>{value}</span>
    </div>
  );
}

function QuickVendorForm({ onCreated }) {
  const [v, setV] = useState({ name: "", contact_person: "", email: "", phone: "", address: "", gstin: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!v.name.trim()) return toast.error("Vendor name required");
    setLoading(true);
    try {
      const { data } = await api.post("/vendors", v);
      toast.success("Vendor added");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider">
        <Buildings size={14} /> New vendor
      </div>
      <Input placeholder="Vendor name *" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className="rounded-sm" data-testid="vendor-name-input" />
      <Input placeholder="Contact person" value={v.contact_person} onChange={(e) => setV({ ...v, contact_person: e.target.value })} className="rounded-sm" />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} className="rounded-sm" />
        <Input placeholder="Phone" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} className="rounded-sm" />
      </div>
      <Input placeholder="GSTIN" value={v.gstin} onChange={(e) => setV({ ...v, gstin: e.target.value })} className="rounded-sm" />
      <Textarea placeholder="Address" value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} className="rounded-sm min-h-[60px]" />
      <Button className="w-full rounded-sm bg-primary hover:bg-[#0039CC]" onClick={submit} disabled={loading} data-testid="save-vendor-btn">
        {loading ? "Saving…" : "Save Vendor"}
      </Button>
    </div>
  );
}
